import { DateTime, Info, Interval } from 'luxon';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import {
  CreateProductPriceRequest,
  DurationUnit,
  EditProductPriceRequest,
  RecurringType,
  RentalProductPrice,
} from '../../../clients/quipli-service/pricing/pricing';
import { Field } from '../../../components/field/field';
import './rental-price-modal.scss';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { Modal } from '../../../components/modal/modal';
import { addNumericSuffix } from '../../../util/util';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../util/toasts';
import { ApiError } from '../../../clients/axios-instance';
import { Typography } from '../../../components/typography/typography';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { OrderType } from '../../../clients/quipli-service/order/order';
import { Button } from '../../../components/form/button';

type RecurringOption = {
  label: string;
  value: string;
  recurringType: RecurringType;
  recurringDaysOfWeek?: number[]; // 1-7 where 1 is Monday and 7 is Sunday
  recurringWeeksOfMonth?: number[]; // -1 or 1-5 where -1 means last week of month
  recurringDaysOfMonth?: number[]; // -1 or 1-31 where -1 means last day of month
};

export const RentalPriceModal = ({
  productId,
  price,
  isOpen,
  close,
  locationOptions,
}: {
  productId: number;
  price?: RentalProductPrice;
  isOpen: boolean;
  close: (refresh?: boolean) => void;
  locationOptions: { value: number; label: string }[];
}) => {
  const { createPrice, editPrice } = useAPI();

  const initPriceState = { productId, showPriceOnProductCard: true };
  const [priceState, setPriceState] = useState<{
    id?: string;
    productId: number;
    durationUnit?: { label: string; value: DurationUnit };
    durationValue?: number;
    price?: number;
    from?: DateTime;
    to?: DateTime;
    showPriceOnProductCard: boolean;
    name?: string;
  }>(initPriceState);
  const [selectedRecurringOption, setSelectedRecurringOption] = useState<RecurringOption>();
  const [recurringOptions, setRecurringOptions] = useState<RecurringOption[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<{ value: number; label: string }[]>([]);
  const initDaysOfWeek = [
    { label: 'S', value: 7, selected: false },
    { label: 'M', value: 1, selected: false },
    { label: 'T', value: 2, selected: false },
    { label: 'W', value: 3, selected: false },
    { label: 'T', value: 4, selected: false },
    { label: 'F', value: 5, selected: false },
    { label: 'S', value: 6, selected: false },
  ];
  const [daysOfWeek, setDaysOfWeek] = useState(initDaysOfWeek);
  const [isSaving, setIsSaving] = useState(false);
  const [isLocationSpecific, setIsLocationSpecific] = useState<boolean>(
    price && price.locationIds ? price.locationIds.length > 0 : false,
  );

  useEffect(() => {
    if (price) {
      setPriceState({
        id: price.id,
        productId,
        durationUnit: unitOptions.find((u) => u.value === price.durationUnit),
        durationValue: price.durationValue,
        price: price.price / 100,
        from: price.from,
        to: price.to,
        showPriceOnProductCard: price.featured,
        name: price.name,
      });
      setIsLocationSpecific(price && price.locationIds ? price.locationIds.length > 0 : false);
      setSelectedLocations(locationOptions.filter((option) => price.locationIds?.includes(option.value)));
    } else {
      resetState();
    }
  }, [price]);

  const translateDaysOfWeek = (dow: number[]): string =>
    dow.length === 7 ? 'week' : dow.map((day) => Info.weekdays('short')[day - 1]).join(', ');

  useEffect(() => {
    const firstOfMonth = priceState.from?.set({ day: 1 });
    const weekNumber = priceState.from
      ? (priceState.from.month == 1 && priceState.from.weekNumber > 50
          ? 0
          : priceState.from.weekNumber < firstOfMonth!.weekNumber
          ? 53
          : priceState.from.weekNumber) -
        (priceState.from.month == 1 && firstOfMonth!.weekNumber > 50 ? 0 : firstOfMonth!.weekNumber) +
        (firstOfMonth!.weekday <= priceState.from.weekday ? 1 : 0)
      : -1;
    const rc = [
      {
        label: 'Daily',
        value: 'Daily',
        recurringType: undefined,
        recurringDaysOfWeek: undefined,
        recurringDaysOfMonth: undefined,
        recurringWeeksOfMonth: undefined,
      },
      {
        label: 'Every week',
        value: 'Every week',
        recurringType: RecurringType.Weekly,
        recurringDaysOfWeek:
          selectedRecurringOption?.recurringDaysOfWeek ||
          price?.recurringDaysOfWeek ||
          (priceState?.from?.weekday ? [priceState.from.weekday] : []),
      },
      {
        label: 'Every other week',
        value: 'Every other week', // TODO: maybe biweekly shouldn't require from date...
        recurringType: RecurringType.Biweekly,
        recurringDaysOfWeek:
          selectedRecurringOption?.recurringDaysOfWeek ||
          price?.recurringDaysOfWeek ||
          (priceState?.from?.weekday ? [priceState.from.weekday] : []),
      },
      priceState?.from?.day
        ? {
            label: `Every month on the ${addNumericSuffix(priceState.from.day)}`,
            value: `Every month on the nth day`,
            recurringType: RecurringType.DateMonthly,
            recurringDaysOfMonth: [priceState.from.day],
          }
        : undefined,
      priceState?.from && priceState.from.plus({ days: 1 }).day === 1
        ? {
            label: 'Every last day of the month',
            value: 'Every last day of the month',
            recurringType: RecurringType.DateMonthly,
            recurringDaysOfMonth: [-1],
          }
        : undefined,
      priceState?.from?.day
        ? {
            // every 1st wednesday of the month
            label: `Every ${addNumericSuffix(weekNumber)} ${translateDaysOfWeek([
              priceState.from.weekday,
            ])} of the month`,
            value: `Every nth blank-day of the month`,
            recurringType: RecurringType.DayOfWeekMonthly,
            recurringDaysOfWeek: [priceState.from.weekday],
            recurringWeeksOfMonth: [weekNumber],
          }
        : undefined,
      priceState?.from &&
      priceState.from
        .endOf('month')
        .minus({ days: priceState.from.endOf('month').minus({ days: priceState.from.weekday - 1 }).weekday - 1 })
        .toISODate() === priceState.from.endOf('day').toISODate()
        ? {
            // every last x of the month
            label: `Every last ${translateDaysOfWeek([priceState.from.weekday])} of the month`,
            value: `Every last blank-day of the month`,
            recurringType: RecurringType.DayOfWeekMonthly,
            recurringDaysOfWeek: [priceState.from.weekday],
            recurringWeeksOfMonth: [-1],
          }
        : undefined,
    ].filter((v) => v !== undefined) as RecurringOption[];
    setRecurringOptions(rc);
  }, [priceState.from]);

  function isSameArray(a: number[] | undefined, b: number[] | undefined): boolean {
    return a == b || (!!a && !!b && a.length === b.length && a.every((val, index) => val === b[index]));
  }
  // pre-select recurring option for editing price
  useEffect(() => {
    if (price) {
      setSelectedRecurringOption(
        recurringOptions.find((o) => {
          return (
            o.recurringType == price.recurringType &&
            isSameArray(o.recurringDaysOfWeek, price.recurringDaysOfWeek) &&
            isSameArray(o.recurringDaysOfMonth, price.recurringDaysOfMonth) &&
            isSameArray(o.recurringWeeksOfMonth, price.recurringWeeksOfMonth)
          );
        }),
      );
    }
  }, [price, recurringOptions]);

  useEffect(() => {
    if (
      selectedRecurringOption &&
      ![RecurringType.Weekly, RecurringType.Biweekly].includes(selectedRecurringOption.recurringType)
    ) {
      const newSelectedOption =
        selectedRecurringOption!.value === 'Every last day of the month'
          ? recurringOptions.find((o) => o.value === 'Every month on the nth day')
          : selectedRecurringOption!.value === 'Every last blank-day of the month'
          ? recurringOptions.find((o) => o.value === 'Every nth blank-day of the month')
          : recurringOptions.find((o) => o.value === selectedRecurringOption?.value);
      setSelectedRecurringOption(newSelectedOption);
    }
  }, [recurringOptions]);

  useEffect(() => {
    if (selectedRecurringOption?.recurringDaysOfWeek) {
      setDaysOfWeek(
        daysOfWeek.map((dow) => {
          return { ...dow, selected: selectedRecurringOption!.recurringDaysOfWeek!.includes(dow.value) };
        }),
      );
    }
  }, [selectedRecurringOption?.recurringDaysOfWeek]);

  const unitOptions = [
    { label: 'Per hour', value: DurationUnit.hour },
    { label: 'Per day', value: DurationUnit.day },
    { label: 'Per week', value: DurationUnit.week },
    { label: 'Per month', value: DurationUnit.month },
    { label: 'Per year', value: DurationUnit.year },
  ];

  const isSaveDisabled: boolean =
    !priceState.durationUnit ||
    !priceState.durationValue ||
    priceState.price === undefined ||
    !selectedRecurringOption ||
    (!priceState.from &&
      [RecurringType.Biweekly, RecurringType.DateMonthly, RecurringType.DayOfWeekMonthly].includes(
        selectedRecurringOption.recurringType,
      )) ||
    ([RecurringType.Weekly, RecurringType.Biweekly, RecurringType.DayOfWeekMonthly].includes(
      selectedRecurringOption?.recurringType,
    ) &&
      (selectedRecurringOption.recurringDaysOfWeek?.length || 0) < 1) ||
    (selectedRecurringOption?.recurringType === RecurringType.DayOfWeekMonthly &&
      (selectedRecurringOption?.recurringWeeksOfMonth?.length || 0) < 1) ||
    (selectedRecurringOption?.recurringType === RecurringType.DateMonthly &&
      (selectedRecurringOption?.recurringDaysOfMonth?.length || 0) < 1);

  const resetState = () => {
    setPriceState(initPriceState);
    setSelectedRecurringOption(undefined);
    setSelectedLocations([]);
    setIsLocationSpecific(false);
  };

  const savePrice = () => {
    if (isSaveDisabled) {
      throw new Error('Missing required fields to save rental price');
    }
    setIsSaving(true);
    const body = {
      type: OrderType.Rental,
      productId: priceState.productId,
      durationUnit: priceState.durationUnit!.value,
      durationValue: priceState.durationValue!,
      price: +(priceState.price! * 100).toFixed(0),
      from: priceState.from?.startOf('day'),
      to: priceState.to?.endOf('day'),
      recurringType: selectedRecurringOption?.recurringType,
      recurringDaysOfWeek: selectedRecurringOption?.recurringDaysOfWeek,
      recurringDaysOfMonth: selectedRecurringOption?.recurringDaysOfMonth,
      recurringWeeksOfMonth: selectedRecurringOption?.recurringWeeksOfMonth,
      name: priceState.name,
      featured: !selectedRecurringOption?.recurringType && priceState.showPriceOnProductCard, // only allow daily prices to be shown on product card (for now...)
      locationIds: isLocationSpecific ? selectedLocations.map((l) => l.value) : undefined,
    };
    (price?.id ? editPrice(price.id, body as EditProductPriceRequest) : createPrice(body as CreateProductPriceRequest))
      .then(() => {
        toast('Successfully created rental price', 'success');
        resetState();
        close(true);
      })
      .catch((err: ApiError) => {
        const errMsg = err.status === 400 && err.message ? err.message : 'Error creating rental price';
        toast(errMsg, 'error');
      })
      .finally(() => setIsSaving(false));
  };

  const cancel = () => {
    resetState();
    close();
  };

  const beforeNow = Interval.fromDateTimes(DateTime.now().minus({ years: 100 }), DateTime.now());
  const beforeFrom = priceState.from
    ? Interval.fromDateTimes(DateTime.now().minus({ years: 100 }), priceState.from)
    : beforeNow;

  return (
    <Modal isOpen={isOpen} title="Add rental price" close={cancel} className="rental-price-modal">
      <div className="space-y-5 edit-rental-price-container font-sans">
        <div>
          <Field
            type="text"
            label="Name"
            value={priceState.name}
            onChange={(name: string) => setPriceState({ ...priceState, name })}
          />
        </div>

        <div className="flex text-gray-800">
          <Checkbox
            isChecked={isLocationSpecific}
            onChange={(isChecked) => setIsLocationSpecific(isChecked)}
            label="This price applies to specific location(s)"
          />
        </div>

        {isLocationSpecific && (
          <div className="grid grid-cols-5 items-center gap-2">
            <span className="col-span-2 ml-1 text-sm font-semibold text-gray-500">Locations</span>
            <div className="col-span-3">
              <Select
                id="locations-select"
                isMulti={true}
                options={locationOptions}
                value={selectedLocations}
                onChange={(selected) => {
                  setSelectedLocations(selected as { value: number; label: string }[]);
                }}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    margin: '0px',
                    padding: '0px',
                    fontStyle: 'normal',
                    borderColor: 'rgb(209 213 219)',
                  }),
                }}
              />
            </div>
          </div>
        )}
        <div className="grid grid-cols-5 gap-2">
          <div className="unit-select col-span-2">
            <span className="ml-1 text-sm font-semibold text-gray-500">Unit*</span>
            <Select
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  height: '40px',
                  margin: '0px',
                  padding: '0px',
                  fontStyle: 'normal',
                  borderColor: 'rgb(209 213 219)',
                }),
              }}
              className="mt-1"
              options={unitOptions}
              value={priceState.durationUnit}
              onChange={(newValue) => setPriceState({ ...priceState, durationUnit: newValue || undefined })}
            />
          </div>

          <div className="col-span-1">
            <Field
              className="quantity-field"
              label="qty.*"
              type="number"
              value={priceState.durationValue}
              onChange={(durationValue: number) => setPriceState({ ...priceState, durationValue })}
              maximumFractionDigits={0}
            />
          </div>
          <div className="col-span-2">
            <Field
              className="price-field"
              type="number"
              label="Rental price, $*"
              value={priceState.price}
              onChange={(p: number) => {
                setPriceState({ ...priceState, price: p });
              }}
              maximumFractionDigits={2}
            />
          </div>
        </div>

        <div className="grid grid-cols-5 items-center gap-2">
          <div className="col-span-2 ml-1">
            <span className="text-sm font-semibold text-gray-500">Starts On</span>
          </div>
          <div className="col-span-3">
            <DatePicker
              disabledIntervals={[beforeNow]}
              value={priceState.from}
              onChange={(from) => setPriceState({ ...priceState, from })}
            />
          </div>
        </div>

        <div className="grid grid-cols-5 items-center gap-2">
          <div className="col-span-2 ml-1">
            <span className="text-sm font-semibold text-gray-500">Ends On</span>
          </div>
          <div className="col-span-3">
            <DatePicker
              disabledIntervals={[beforeFrom]}
              value={priceState.to}
              onChange={(to) => setPriceState({ ...priceState, to })}
            />
          </div>
        </div>

        <div className="grid grid-cols-5 items-center gap-2">
          <span className="col-span-2 ml-1 text-sm font-semibold text-gray-500">Repeat Price*</span>
          <div className="col-span-3">
            <Select
              className="repeat-price-select"
              options={recurringOptions}
              value={selectedRecurringOption}
              onChange={(selected) => setSelectedRecurringOption(selected || undefined)}
              maxMenuHeight={200}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  height: '40px',
                  margin: '0px',
                  padding: '0px',
                  fontStyle: 'normal',
                  borderColor: 'rgb(209 213 219)',
                }),
                menuList: (base) => ({
                  ...base,

                  '::-webkit-scrollbar': {
                    width: '4px',
                    height: '0px',
                  },
                  '::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                  },
                  '::-webkit-scrollbar-thumb': {
                    background: '#888',
                  },
                  '::-webkit-scrollbar-thumb:hover': {
                    background: '#555',
                  },
                }),
              }}
            />
          </div>
        </div>

        {selectedRecurringOption &&
          [RecurringType.Weekly, RecurringType.Biweekly].includes(selectedRecurringOption.recurringType) && (
            <div className="day-of-week-container mr-2 grid grid-cols-5 items-center gap-2">
              <span className="col-span-2 ml-1 text-sm font-semibold text-gray-500">On*</span>
              <div className="col-span-3 flex justify-between">
                {daysOfWeek.map((dow) => (
                  <div
                    className={`rounded-full ${
                      dow.selected ? 'bg-quipli-yellow-300' : 'bg-gray-200'
                    } h-8 w-8 text-center hover:cursor-pointer`}
                    key={dow.value}
                    onClick={() => {
                      setSelectedRecurringOption({
                        ...selectedRecurringOption!,
                        recurringDaysOfWeek: dow.selected
                          ? selectedRecurringOption?.recurringDaysOfWeek?.filter((v) => v != dow.value)
                          : [...(selectedRecurringOption?.recurringDaysOfWeek || []), dow.value],
                      });
                    }}
                  >
                    <span className="align-sub leading-loose">{dow.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        {selectedRecurringOption?.label === 'Daily' && (
          <div className="flex text-gray-800">
            <Checkbox
              label="Show this price on product card"
              isChecked={priceState.showPriceOnProductCard}
              onChange={() =>
                setPriceState({ ...priceState, showPriceOnProductCard: !priceState.showPriceOnProductCard })
              }
            />
          </div>
        )}
      </div>

      <div>
        <Typography type="text-sm">
          Include start & end date if this pricing configuration pertains to a given period of time. If you'd like to
          configure a price that repeats on a specific day of the month (ie: Every month on the 28th), a start date is
          required.{' '}
        </Typography>
      </div>
      <div className="inset-x-0 bottom-0 mt-8 flex justify-end button-group">
        <Button className="button-secondary" onClick={cancel}>
          <Typography type="text-sm">Cancel</Typography>
        </Button>
        <Button className="button-primary" onClick={savePrice} disabled={isSaveDisabled || isSaving}>
          Save
        </Button>
      </div>
    </Modal>
  );
};
